import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { FIELD, UNSET } from '../theme/tokens';

/**
 * A labelled picker — the platform's one "choose a thing" field.
 *
 * ── WHERE THE DESIGN COMES FROM ─────────────────────────────────────────────────────────
 *
 * The Execution Control Center, and nothing else. Every value below was copied from
 * `ExecutionControlCenter.css` and can be cited:
 *
 *   `.pm-ecc .dl-fg`        display:flex; align-items:center; gap:8px
 *   `.pm-ecc .dl-fg label`  font-weight:600; color:#455a64   (13px inherited from .ecc-toolbar)
 *   `.pm-ecc .ecc-sel`      padding:7px 12px; border:1px solid #d5dde3; border-radius:6px;
 *                           font-size:13px; background:#fff; cursor:pointer
 *
 * They live in `theme/tokens.ts` as FIELD so the design is stated once. Nothing here invents a
 * number — if a value needs to change, it changes in the tokens.
 *
 * ── WHY THIS IS NOT A NATIVE <select> ───────────────────────────────────────────────────
 *
 * Not because of the TRIGGER — as of 2026-09-29 the trigger is deliberately one fixed line
 * that cuts with an ellipsis, which a native select does too (see `clampLines`). It is the
 * PANEL that a native select cannot do: the longest project option in this database is 333
 * characters, and a native `<option>` is one unwrappable line with no control over its width.
 * Here the panel wraps, runs to `min(880px, 92vw)`, and is where the whole text is read —
 * so the field keeps one shape on every screen and nothing is unreadable.
 *
 * A native `<select multiple>` is no better: it renders as a permanently-open scrolling box of
 * a fixed height, not a dropdown, and behaves differently on every platform. That is why this
 * codebase already hand-rolled `PxFilter` (Portfolio Exceptions) and `mdsel` (Meeting Decks).
 * This is the same pattern, made shared.
 *
 * ── THE THREE MODES ─────────────────────────────────────────────────────────────────────
 *
 *   SINGLE       `multiple` omitted. One value. The PROMPT IS ALWAYS THE FIRST ROW, and it is
 *                selectable, so the field can return to "nothing chosen".
 *
 *                It carries `UNSET`, not '', because on several screens '' is already a real
 *                choice ("All Projects"). An earlier version of this component suppressed the
 *                prompt whenever a screen owned '' — which meant Project Creation opened
 *                reading "All Projects" and the prompt was never visible. That was wrong: the
 *                two are different statements and both must be on the list.
 *
 *                `allowClear={false}` drops the prompt row, for a REQUIRED field only.
 *   MULTIPLE     `multiple`. An array of values. NO CHECKBOXES — a plain click picks just
 *                that one and closes; Ctrl-click (Cmd on a Mac) adds or removes, leaving the
 *                panel open. This is the source's `toggleProjPick` rule, and it is what keeps
 *                a multi-select field looking exactly like a single-select one: the Project
 *                field must not change appearance from screen to screen.
 *   SELECT ALL   `selectAll` (on by default in multiple mode) adds an all/none row, and the
 *                trigger collapses to `allLabel` once everything is picked.
 *
 * A screen chooses its mode; the component never decides for it. Turning a single-select
 * filter into a multi-select one changes what the server is asked for, and that is a screen's
 * decision, not a styling one.
 */

export type SelectFieldWidth = keyof typeof FIELD.widths;

export interface SelectFieldOption {
  /** Submitted value. An empty string can be a legitimate choice — "All Projects" is one. */
  value: string;
  /**
   * Plain text for a row that is not an id/description pair — "All Projects", the prompt.
   * Optional when `id` is given; it is then derived, so the two can never disagree.
   */
  label?: string;
  /**
   * The project or opportunity id. Rendered BOLD, ahead of the description, in the field and
   * in the list alike — the id is what is scanned for, so it carries the weight.
   */
  id?: string;
  /**
   * The description, rendered after the id and a hyphen, in normal weight.
   * `null` is accepted because that is what the API returns for "no description" — forcing
   * every call site to write `?? undefined` would be noise, and one of them would forget.
   */
  description?: string | null;
  disabled?: boolean;
}

/** The plain-text form of an option — for the trigger, and for anything that needs a string. */
function optionText(o: SelectFieldOption): string {
  if (o.id != null) return o.id + (o.description ? ` - ${o.description}` : '');
  return o.label ?? '';
}

/** The rendered form: bold id, hyphen, description. */
function OptionBody({ o }: { o: SelectFieldOption }) {
  if (o.id == null) return <>{o.label}</>;
  return (
    <>
      <strong style={{ fontWeight: 700 }}>{o.id}</strong>
      {o.description ? ` - ${o.description}` : ''}
    </>
  );
}

interface SelectFieldBaseProps {
  label: ReactNode;
  options: SelectFieldOption[];
  /** Shown when nothing is chosen. Use `PROMPT.project` / `PROMPT.opportunity`. */
  placeholder?: string;
  width?: SelectFieldWidth;
  orientation?: 'inline' | 'stacked';
  /**
   * Cap the trigger at N lines. **1 (the default) — the field never changes shape.**
   *
   * Owner ruling, 2026-09-29: *"I do not need to read entire Opportunity description. it
   * can be hidden ... whatever content fits in the given opportunity/project decription
   * field. I do not need dynamic adjustment."* A description that does not fit is cut with
   * an ellipsis; the OPEN PANEL is where the whole text is read, and it still wraps in full.
   *
   * 0 restores the old un-truncated wrapping trigger. Nothing passes it.
   */
  clampLines?: number;
  help?: ReactNode;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  /**
   * Open the list in a fixed-position layer measured from the field, instead of hanging it
   * absolutely below the field. For screens whose toolbar sits inside `overflow: hidden`
   * containers (Program Planning's screen lock), where an absolute panel is cut off at the
   * container's edge. Default off: every existing screen keeps today's behaviour.
   */
  floating?: boolean;
}

export interface SelectFieldSingleProps extends SelectFieldBaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
  /**
   * Render the prompt as a selectable first row, so the field can be cleared again.
   * Default true. Automatically suppressed when the caller already supplies an option whose
   * value is '' — on those screens blank means "All", and a second blank row would be both a
   * duplicate and a lie.
   */
  allowClear?: boolean;
}

export interface SelectFieldMultiProps extends SelectFieldBaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
  /** Show the all/none row. Default true. */
  selectAll?: boolean;
  /** Trigger text when everything is ticked. Defaults to "All". */
  allLabel?: string;
}

export type SelectFieldProps = SelectFieldSingleProps | SelectFieldMultiProps;

export function SelectField(props: SelectFieldProps) {
  const {
    label,
    options,
    placeholder = 'Select',
    width = 'md',
    orientation = 'inline',
    clampLines = 1,
    help,
    error = false,
    disabled = false,
    id,
    className,
    style,
    floating = false,
  } = props;

  const multiple = props.multiple === true;
  // Narrow ONCE, here, so the rest of the component reads props through a typed handle rather
  // than re-testing `props.multiple` in every branch (which TypeScript cannot follow).
  const single = props.multiple === true ? null : props;
  const multi = props.multiple === true ? props : null;
  const reactId = useId();
  const controlId = id ?? `sf-${reactId}`;
  const listId = `${controlId}-list`;
  const inline = orientation === 'inline';

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [alignRight, setAlignRight] = useState(false);
  const [fixedAt, setFixedAt] = useState<{ top: number; left: number; right: number; width: number; font: string } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  /**
   * THE VALUE THE PROMPT ROW CARRIES.
   *
   * '' on the great majority of screens, where '' has always meant "nothing chosen" — so
   * those screens keep the exact semantics they were written against and need no change.
   *
   * UNSET only where the screen has claimed '' for a REAL choice ("All Projects", "All
   * (project-wide)", "None"). There the two must be told apart, and the screen normalises
   * UNSET back to its own default. Emitting UNSET everywhere instead would have quietly
   * handed a `__unset__` string to twenty-four unprepared screens.
   */
  const promptValue = useMemo(
    () => (options.some((o) => o.value === '') ? UNSET : ''),
    [options],
  );

  // The prompt is ALWAYS the first row in single mode — never conditional on what the screen
  // happens to use for ''. Only an explicitly required field opts out.
  const rows = useMemo<SelectFieldOption[]>(() => {
    if (multiple) return options;
    return single?.allowClear !== false
      ? [{ value: promptValue, label: placeholder }, ...options]
      : options;
  }, [multiple, options, placeholder, promptValue, single]);

  const selectedValues = useMemo<string[]>(
    () => multi?.value ?? [],
    [multi],
  );

  const pickable = useMemo(() => rows.filter((o) => !o.disabled), [rows]);
  const allPicked = multiple && pickable.length > 0 && pickable.every((o) => selectedValues.includes(o.value));

  const singleIndex = single ? rows.findIndex((o) => o.value === single.value) : -1;
  const singleSelected = singleIndex >= 0 ? rows[singleIndex] : undefined;

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setActive(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !panelRef.current?.contains(t)) close(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, close]);

  // Decide which edge the panel hangs from, measured rather than guessed: a field in the
  // right-hand half of the window gets a right-anchored panel so a wide list stays on screen.
  useEffect(() => {
    if (!open) return;
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const widest = Math.min(880, window.innerWidth * 0.92);
    setAlignRight(r.left + widest > window.innerWidth - 8 && r.right > widest);
  }, [open]);

  // `floating`: follow the field while the list is open (the page or a panel may scroll).
  useEffect(() => {
    if (!open || !floating) {
      setFixedAt(null);
      return;
    }
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      // The layer sits on <body>, so it takes the field's font rather than the page default.
      if (r && triggerRef.current) setFixedAt({ top: r.bottom + 4, left: r.left, right: window.innerWidth - r.right, width: r.width, font: getComputedStyle(triggerRef.current).fontFamily });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, floating]);

  useEffect(() => {
    if (!open || active < 0) return;
    panelRef.current
      ?.querySelector<HTMLElement>(`[data-i="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const step = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < rows.length; i += dir) {
      if (!rows[i]?.disabled) return i;
    }
    return from;
  };

  const choose = (o: SelectFieldOption, additive = false) => {
    if (o.disabled) return;
    if (multiple) {
      if (additive) {
        // Ctrl/Cmd-click — add or remove, and stay open so several can be picked in a row.
        const cur = selectedValues;
        multi?.onChange(cur.includes(o.value) ? cur.filter((v) => v !== o.value) : [...cur, o.value]);
      } else {
        // Plain click — just this one, applied now. The source's rule.
        multi?.onChange([o.value]);
        close(true);
      }
    } else {
      single?.onChange(o.value);
      close(true);
    }
  };

  const toggleAll = () => {
    if (!multiple) return;
    multi?.onChange(allPicked ? [] : pickable.map((o) => o.value));
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
        setActive(singleIndex >= 0 ? singleIndex : step(-1, 1));
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => step(a < 0 ? -1 : a, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => step(a < 0 ? rows.length : a, -1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(step(-1, 1));
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(step(rows.length, -1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const o = rows[active];
      // Space behaves like Ctrl-click (add/remove); Enter like a plain click (just this one).
      if (o) choose(o, multiple && e.key === ' ');
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  // `floating`: the open list is drawn in a layer on <body>, fixed to the window at the
  // field's measured position. Inside the field it can be clipped by an `overflow: hidden`
  // ancestor, covered by a later stacking context, or shifted by an ancestor that is a
  // containing block (the shell's content box has `container-type`) — all three happened on
  // Program Planning's locked screens. Keyboard focus stays on the field's button.
  const floatIt = (panel: ReactNode) => (floating && fixedAt ? createPortal(panel, document.body) : floating ? null : panel);

  // What the trigger reads.
  const triggerText = multiple
    ? allPicked
      ? (multi?.allLabel ?? 'All')
      : selectedValues.length
        ? rows.filter((o) => selectedValues.includes(o.value)).map(optionText).join(', ')
        : placeholder
    : singleSelected
      ? optionText(singleSelected)
      : placeholder;

  const isPlaceholder = multiple
    ? selectedValues.length === 0
    : !singleSelected || singleSelected.value === promptValue;
  const borderColor = error ? FIELD.errorColor : FIELD.controlBorder;

  const clamp: CSSProperties =
    /*
     * ONE LINE IS THE ORDINARY CASE, and it is plain `text-overflow`, not a line clamp:
     * `-webkit-line-clamp: 1` leaves the box in webkit-box layout, where a trailing ellipsis
     * is a rendering detail rather than a guarantee. `nowrap` + `ellipsis` is the CSS that
     * actually specifies the behaviour, and it needs the `min-width: 0` the span already has.
     */
    clampLines === 1
      ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
      : clampLines > 1
      ? { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: clampLines, overflow: 'hidden' }
      : {};

  const rowStyle = (i: number, picked: boolean, dis?: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '7px 12px',
    fontSize: FIELD.controlFontSize,
    lineHeight: 1.45,
    whiteSpace: 'normal',
    cursor: dis ? 'not-allowed' : 'pointer',
    color: dis ? '#9aa5ad' : FIELD.controlColor,
    background: i === active ? '#eef3f7' : picked ? '#f6f9fb' : 'transparent',
    fontWeight: picked ? 600 : 400,
  });

  return (
    <div
      ref={rootRef}
      className={className}
      style={{
        display: inline ? 'flex' : 'inline-flex',
        flexDirection: inline ? 'row' : 'column',
        /*
         * AN INLINE LABEL CENTRES ON THE CONTROL IT NAMES.
         *
         * This was `flex-start` plus `paddingTop: 8` on the label — a hand-tuned approximation
         * of centring a ~19px label against the 36px control. It worked only while nothing
         * touched `alignItems`, and more than ten call sites across three products pass
         * `style={{ alignItems: 'center' }}` precisely because the label looked wrong without
         * it. Those two corrections COMPOUND: the flex box centres the label and the padding
         * then pushes it 8px below the centre line, which is the misalignment the owner
         * reported ("they sit below the central line") and the reason he reported it on more
         * than one screen.
         *
         * Centring here and dropping the padding fixes every call site at once, including the
         * ones that never knew to compensate. A call site that still passes
         * `alignItems: 'center'` now agrees with the default instead of fighting it.
         */
        alignItems: inline ? 'center' : 'stretch',
        flexWrap: 'nowrap',
        gap: inline ? FIELD.labelGap : 5,
        ...style,
      }}
    >
      <label
        htmlFor={controlId}
        style={{
          fontSize: FIELD.labelFontSize,
          fontWeight: FIELD.labelFontWeight,
          color: error ? FIELD.errorColor : FIELD.labelColor,
          whiteSpace: 'nowrap',
          cursor: disabled ? 'default' : 'pointer',
          /* No vertical nudge: the row centres the label against the control (see above). */
        }}
      >
        {label}
      </label>

      <div style={{ position: 'relative', width: FIELD.widths[width], maxWidth: '100%', minWidth: 0 }}>
        <button
          id={controlId}
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-haspopup="listbox"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setOpen((o) => !o);
            setActive(singleIndex >= 0 ? singleIndex : step(-1, 1));
          }}
          onKeyDown={onKeyDown}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            textAlign: 'left',
            /* §1.9.2's 36px. A multi-line selection still grows past it — this is the floor
               that keeps a single-line picker level with anything beside it. */
            minHeight: FIELD.controlHeight,
            boxSizing: 'border-box',
            padding: FIELD.controlPadding,
            border: `1px solid ${borderColor}`,
            borderRadius: FIELD.controlRadius,
            fontSize: FIELD.controlFontSize,
            fontFamily: 'inherit',
            lineHeight: 1.45,
            color: isPlaceholder ? FIELD.helpColor : FIELD.controlColor,
            background: disabled ? '#f5f7f9' : FIELD.controlBackground,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'normal', ...clamp }}>
            {multiple || !singleSelected ? triggerText : <OptionBody o={singleSelected} />}
          </span>
          <span aria-hidden="true" style={{ flex: 'none', color: '#5f6368', paddingTop: 1 }}>
            ▾
          </span>
        </button>

        {open ? floatIt(
          <div
            ref={panelRef}
            id={listId}
            role="listbox"
            aria-multiselectable={multiple || undefined}
            aria-activedescendant={active >= 0 ? `${controlId}-o${active}` : undefined}
            style={{
              /* A panel wider than its field runs off the right edge when the field sits near
                 it, so it anchors to whichever side leaves it on screen. */
              ...(floating && fixedAt
                ? {
                    position: 'fixed',
                    zIndex: 1700,
                    fontFamily: fixedAt.font,
                    top: fixedAt.top,
                    ...(alignRight ? { right: fixedAt.right } : { left: fixedAt.left }),
                  }
                : {
                    position: 'absolute',
                    zIndex: 60,
                    top: 'calc(100% + 4px)',
                    ...(alignRight ? { right: 0 } : { left: 0 }),
                  }),
              /*
               * THE PANEL IS ALLOWED TO BE WIDER THAN THE FIELD, and the field is not allowed
               * to change. A project description runs to a few hundred characters; widening
               * the field to suit would make every screen's toolbar a different shape. So the
               * list expands sideways and wraps, and the field keeps its one width.
               */
              minWidth: floating && fixedAt ? fixedAt.width : '100%',
              width: 'max-content',
              maxWidth: 'min(880px, 92vw)',
              maxHeight: 320,
              overflowY: 'auto',
              background: FIELD.controlBackground,
              border: `1px solid ${FIELD.controlBorder}`,
              borderRadius: FIELD.controlRadius,
              boxShadow: '0 6px 18px rgba(15, 32, 45, 0.14)',
            }}
          >
            {multi && multi.selectAll !== false && pickable.length > 1 ? (
              <div
                onClick={toggleAll}
                role="option"
                aria-selected={allPicked}
                style={{
                  ...rowStyle(-2, false),
                  borderBottom: `1px solid ${FIELD.controlBorder}`,
                  fontWeight: 600,
                  position: 'sticky',
                  top: 0,
                  background: '#fff',
                }}
              >
                <span aria-hidden="true" style={{ flex: 'none', width: 12, color: '#1565c0' }}>
                  {allPicked ? '✓' : ''}
                </span>
                <span style={{ flex: '1 1 auto' }}>{allPicked ? 'Clear all' : 'Select all'}</span>
                <span style={{ flex: 'none', fontWeight: 400, fontSize: 11, color: '#78909c' }}>
                  Ctrl-click to pick several
                </span>
              </div>
            ) : null}

            {rows.map((o, i) => {
              const picked = multiple ? selectedValues.includes(o.value) : o.value === single?.value;
              return (
                <div
                  key={`${o.value}-${i}`}
                  id={`${controlId}-o${i}`}
                  data-i={i}
                  role="option"
                  aria-selected={picked}
                  aria-disabled={o.disabled || undefined}
                  onPointerEnter={() => !o.disabled && setActive(i)}
                  onClick={(e) => choose(o, e.ctrlKey || e.metaKey)}
                  // On a Mac, Control-click is a right-click: the browser fires `contextmenu`
                  // and never `click`, so "Ctrl-click to pick several" did nothing there.
                  // Treat it as the additive click it was meant to be.
                  onContextMenu={(e) => {
                    if (!multiple || !e.ctrlKey) return;
                    e.preventDefault();
                    choose(o, true);
                  }}
                  style={rowStyle(i, picked, o.disabled)}
                >
                  {multiple ? (
                    // A tick, not a checkbox — it marks state without turning the list into a
                    // form control.
                    <span aria-hidden="true" style={{ flex: 'none', width: 12, color: '#1565c0' }}>
                      {picked ? '✓' : ''}
                    </span>
                  ) : null}
                  <span style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <OptionBody o={o} />
                  </span>
                </div>
              );
            })}
          </div>,
        ) : null}
      </div>

      {help ? (
        <span style={{ fontSize: FIELD.helpFontSize, color: error ? FIELD.errorColor : FIELD.helpColor }}>
          {help}
        </span>
      ) : null}
    </div>
  );
}
